import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { botApi, type WalkthroughResult, type WalkthroughSpan } from '../../bot/api'
import { selectionPayload, selectionStorage } from '../../bot/chatSelection'
import type { SavedDrawing } from './workbenchState'
import {
  displaySpan,
  drawingSpan,
  replayStartTs,
  walkthroughSpan,
  type DrawingSpan,
} from './walkthroughPeriod'

/** How often to ask the server whether the queued run has finished. */
const POLL_INTERVAL_MS = 3000

/**
 * How long before the overlay stops implying "any second now" and offers a way out.
 * The run is NOT abandoned — polling continues; only the copy changes.
 */
const SLOW_AFTER_MS = 120_000

const STATUS_PENDING = 0
const STATUS_RUNNING = 1
const STATUS_ERROR = 200
const STATUS_CANCELED = 201

/** Why the start overlay is up, which is the only thing its copy differs on. */
export type WalkthroughStartReason = 'start' | 'resume' | 'rebuild'

export type WalkthroughPhase =
  | { kind: 'off' }
  | { kind: 'starting'; reason: WalkthroughStartReason; since: number; runStatus: number | null; progressPct: number }
  | { kind: 'error'; message: string; action?: 'billing' }
  | { kind: 'ready'; run: WalkthroughRun }

/** Everything the replay subtree needs, derived once the run is known-complete. */
export interface WalkthroughRun {
  btid: string
  symbol: string
  timeframe: string
  /**
   * start_date T00:00:00Z, unix seconds — the run's REAL range.
   *
   * Padded by the server with ~200 bars of indicator warm-up and rounded to whole UTC days,
   * so it is much wider than the drawings. Two things need it and nothing else should: the
   * engine's own progress readout (`pctOf`), and the trades-pane event fetch, which has to
   * catch a position opened during warm-up and still held when the drawings begin. Everything
   * the user LOOKS at uses the display span below.
   */
  rangeStartTs: number
  /** end_date T23:59:59Z, unix seconds. See rangeStartTs. */
  rangeEndTs: number
  /** Where playback opens, where scrub-zero lands and where Restart goes, unix seconds. */
  replayStartTs: number
  /** Left bound of what the chart loads, shows and can pan to. unix seconds. */
  displayStartTs: number
  /** Right bound of the same, and the transport's 100%. unix seconds. */
  displayEndTs: number
  status: number
  /**
   * True when the server attached to a run it already had, false when it built this one now.
   *
   * The difference the user feels: a `false` run is one they just watched being prepared, so the
   * replay opens on the far side of a wait and starts itself. See WorkbenchWalkthrough.
   */
  reused: boolean
  progressBacktest: string | null
  progressView: string | null
  /** Which code version this run validates; a change makes it stale. */
  codeId: number | null
  configInput: WalkthroughResult['configInput']
}

/** Why the Walkthrough toggle is unavailable, if it is. */
export type WalkthroughBlockedReason = 'loading' | 'no-code' | 'no-drawings' | 'no-timestamps'

export interface WalkthroughEligibility {
  ok: boolean
  reason: WalkthroughBlockedReason | null
  /** null for transient states, where a tooltip would just flicker. */
  message: string | null
}

const BLOCKED_MESSAGES: Record<Exclude<WalkthroughBlockedReason, 'loading'>, string> = {
  'no-code':
    'Ask the AI to build your strategy first — the walkthrough replays your generated code.',
  'no-drawings':
    'Draw your setup first — the walkthrough replays the period you drew on.',
  'no-timestamps':
    'Your drawings need at least one point on the time axis.',
}

export interface UseWalkthroughSessionOptions {
  bid: string
  /** True when the URL / parent says walkthrough mode is requested. */
  requested: boolean
  /** Called to leave the mode — on a guard failure or the user's own exit. */
  onExit: () => void
  /** Active workbench symbol. Pinned for the life of the run. */
  symbol: string
  /** Active workbench timeframe. Pinned for the life of the run. */
  timeframe: string
  /**
   * Serialized drawings, frozen at the moment the mode was entered. Passing a live value
   * would let a Canvas edit silently change the run's span mid-session.
   */
  drawings: SavedDrawing[] | null
  /** Whether the bot has generated code at all. */
  hasCode: boolean
  /** Current code version identity; a change invalidates the run. */
  codeId: number | null
  /** False while the workbench is still restoring, so we never snapshot a blank canvas. */
  ready: boolean
}

export interface UseWalkthroughSessionReturn {
  phase: WalkthroughPhase
  eligibility: WalkthroughEligibility
  /** True once we've been up long enough that the user deserves an escape hatch. */
  slow: boolean
  retry: () => void
}

/** unix seconds for the inclusive start of a 'YYYY-MM-DD' UTC day. */
function dayStartTs(date: string): number {
  return Math.floor(Date.parse(`${date}T00:00:00Z`) / 1000)
}

/** unix seconds for the inclusive end of a 'YYYY-MM-DD' UTC day. */
function dayEndTs(date: string): number {
  return Math.floor(Date.parse(`${date}T23:59:59Z`) / 1000)
}

function errorMessage(err: unknown): { message: string; action?: 'billing' } {
  const code = err && typeof err === 'object' && 'errorCode' in err
    ? String((err as { errorCode: unknown }).errorCode)
    : ''

  switch (code) {
    case 'INSUFFICIENT_CREDITS':
      return {
        message: "You've used all of your usage allowance, so the walkthrough can't be prepared.",
        action: 'billing',
      }
    case 'CODE_NOT_FOUND':
      return { message: 'This strategy has no generated code yet.' }
    case 'WALKTHROUGH_NO_DATA':
      return {
        message: 'There is no price history for the period you drew on, so there is nothing to replay.',
      }
    case 'WALKTHROUGH_TIMEFRAME_INVALID':
      return { message: `The walkthrough doesn't support this chart's timeframe.` }
    case 'WALKTHROUGH_SPAN_INVALID':
      return { message: "Couldn't work out a period from your drawings." }
    case 'WALKTHROUGH_ALREADY_STARTING':
      return { message: 'A walkthrough is already being prepared. Give it a moment and try again.' }
    case 'RATE_LIMITED':
      return { message: 'Too many requests just now — wait a moment and try again.' }
    default:
      return { message: "Couldn't prepare the walkthrough." }
  }
}

/**
 * Owns the Canvas → Walkthrough transition: guard, start, wait, hand over.
 *
 * Deliberately does NOT mount the replay stack while the run is in flight. Starting
 * useBacktestChartState against a PENDING row would fetch candles and events for a run that
 * has not produced any, and flash a partial chart before the real one.
 *
 * Nothing about the playback cursor is tracked here. usePlayback already persists it to the
 * row (debounced, on pause, on unmount), so leaving the mode flushes it and re-entering reads
 * it back — which also makes resume survive a page reload for free.
 */
export default function useWalkthroughSession({
  bid,
  requested,
  onExit,
  symbol,
  timeframe,
  drawings,
  hasCode,
  codeId,
  ready,
}: UseWalkthroughSessionOptions): UseWalkthroughSessionReturn {
  const [phase, setPhase] = useState<WalkthroughPhase>({ kind: 'off' })
  // The `since` of the starting phase we have already declared slow. Comparing it against the
  // current phase's `since` makes `slow` derived, so nothing ever has to reset it.
  const [slowFor, setSlowFor] = useState<number | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)

  // Bumped whenever we stop caring about an in-flight start, so a late response can never
  // resurrect a session the user already walked away from.
  const epochRef = useRef(0)

  const span: DrawingSpan | null = drawings ? drawingSpan(drawings) : null

  const eligibility: WalkthroughEligibility = (() => {
    if (!ready || !symbol) return { ok: false, reason: 'loading', message: null }
    if (!hasCode) return { ok: false, reason: 'no-code', message: BLOCKED_MESSAGES['no-code'] }
    // null and [] mean different things: null is "the caller has not frozen a snapshot yet"
    // (transient, and the normal state on the very first render when the mode comes from the
    // URL), while [] really is an empty canvas. Conflating them would bounce every walkthrough
    // entered from a link straight back to Canvas before it could start.
    if (!drawings) return { ok: false, reason: 'loading', message: null }
    if (drawings.length === 0) {
      return { ok: false, reason: 'no-drawings', message: BLOCKED_MESSAGES['no-drawings'] }
    }
    if (!span) {
      return { ok: false, reason: 'no-timestamps', message: BLOCKED_MESSAGES['no-timestamps'] }
    }
    return { ok: true, reason: null, message: null }
  })()

  const onExitRef = useRef(onExit)
  useLayoutEffect(() => {
    onExitRef.current = onExit
  }, [onExit])

  // The code version currently on screen, so the start effect can tell a first run from a
  // rebuild without taking `phase` as a dependency (which would restart it on every beat).
  const readyCodeIdRef = useRef<number | null>(null)
  useLayoutEffect(() => {
    readyCodeIdRef.current = phase.kind === 'ready' ? phase.run.codeId : null
  }, [phase])

  const retry = useCallback(() => {
    // Clearing the error here rather than in an effect keeps the reset in the event that
    // caused it, and lets the start effect re-run purely off the nonce.
    setPhase({ kind: 'off' })
    setRetryNonce((n) => n + 1)
  }, [])

  // ---- Leaving the mode ----------------------------------------------------
  // Resetting rather than deriving `phase` from `requested` is deliberate: a retained 'ready'
  // phase would let re-entry reuse a run object whose progressView is stale (usePlayback
  // flushed the real cursor to the row on unmount), so the replay would reopen at the wrong
  // bar. Clearing forces the fresh read that resume depends on.
  useEffect(() => {
    if (requested) return
    epochRef.current += 1
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting internal state when the external mode flag turns off IS the synchronization
    setPhase({ kind: 'off' })
  }, [requested])

  // ---- Guard ---------------------------------------------------------------
  // A mode requested via a stale ?wt=1 URL (no drawings yet, code not generated) must bounce
  // back to Canvas rather than sit on an overlay that can never resolve.
  useEffect(() => {
    if (!requested) return
    if (eligibility.ok || eligibility.reason === 'loading') return
    onExitRef.current()
  }, [requested, eligibility.ok, eligibility.reason])

  // ---- Start / attach ------------------------------------------------------
  const spanKey = span ? `${span.fromTs}:${span.toTs}` : ''

  useEffect(() => {
    if (!requested || !eligibility.ok || !span || !drawings) return
    if (phase.kind === 'ready' && phase.run.codeId === codeId) return
    if (phase.kind === 'error') return

    const request: WalkthroughSpan | null = walkthroughSpan(symbol, timeframe, drawings)
    if (!request) return

    const epoch = ++epochRef.current
    const reason: WalkthroughStartReason =
      readyCodeIdRef.current != null && readyCodeIdRef.current !== codeId ? 'rebuild' : 'start'

    setPhase({ kind: 'starting', reason, since: Date.now(), runStatus: null, progressPct: 0 })

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const toRun = (result: WalkthroughResult): WalkthroughRun => {
      const period = result.walkthrough?.period
      const bc = result.configInput?.backtest_config ?? {}
      const startDate = period?.startDate ?? bc.start_date
      const endDate = period?.endDate ?? bc.end_date
      const rangeStart = dayStartTs(startDate)
      const rangeEnd = dayEndTs(endDate)
      const display = displaySpan(span, timeframe, rangeStart, rangeEnd)
      return {
        btid: result.btid,
        symbol: result.walkthrough?.symbol ?? bc.symbol ?? symbol,
        timeframe: (result.walkthrough?.timeframe ?? bc.timeframe ?? timeframe).toUpperCase(),
        rangeStartTs: rangeStart,
        rangeEndTs: rangeEnd,
        replayStartTs: replayStartTs(span, timeframe, rangeStart, rangeEnd),
        displayStartTs: display.startTs,
        displayEndTs: display.endTs,
        status: result.status,
        // Absent means "not told", and the safe reading of that is the quiet one: a run we may
        // have been holding all along, which opens paused.
        reused: result.reused ?? true,
        progressBacktest: result.progressBacktest ?? null,
        progressView: result.progressView ?? null,
        codeId,
        configInput: result.configInput,
      }
    }

    const settle = (result: WalkthroughResult) => {
      if (cancelled || epoch !== epochRef.current) return

      if (result.status === STATUS_ERROR || result.status === STATUS_CANCELED) {
        setPhase({
          kind: 'error',
          message: result.resultSummary?.error
            || 'The walkthrough run failed before it produced any results.',
        })
        return
      }

      if (result.status === STATUS_PENDING || result.status === STATUS_RUNNING) {
        const run = toRun(result)
        pollUntilDone(run)
        return
      }

      setPhase({ kind: 'ready', run: toRun(result) })
    }

    // Poll the shared btid-keyed progress endpoint — the same one and the same cadence the
    // Backtest tab uses. No new server surface, and a poll-until-terminal loop is exactly the
    // shape "dismiss the overlay when the run completes" needs.
    const pollUntilDone = (run: WalkthroughRun) => {
      const tick = async () => {
        if (cancelled || epoch !== epochRef.current) return
        try {
          const progress = await botApi.getBacktestProgress(run.btid)
          if (cancelled || epoch !== epochRef.current) return

          const status = progress.status
          if (status === STATUS_ERROR || status === STATUS_CANCELED) {
            const results = await botApi.getBacktestResults(run.btid).catch(() => null)
            if (cancelled || epoch !== epochRef.current) return
            setPhase({
              kind: 'error',
              message:
                (results as unknown as { error?: string } | null)?.error
                || 'The walkthrough run failed before it produced any results.',
            })
            return
          }

          if (status !== STATUS_PENDING && status !== STATUS_RUNNING) {
            setPhase({
              kind: 'ready',
              run: { ...run, status, progressBacktest: progress.progressBacktest ?? null },
            })
            return
          }

          setPhase((prev) => prev.kind === 'starting'
            ? { ...prev, runStatus: status, progressPct: pctOf(progress.progressBacktest, run) }
            : prev)
          timer = setTimeout(tick, POLL_INTERVAL_MS)
        } catch {
          // A transient network blip must not kill a run that is still going; keep polling.
          if (cancelled || epoch !== epochRef.current) return
          timer = setTimeout(tick, POLL_INTERVAL_MS)
        }
      }
      void tick()
    }

    // The walkthrough's one AI call generates a backtest configuration, so it
    // honours the same model the user picked for the chat. Read at call time
    // rather than held in state: this hook has no reason to re-render on it.
    botApi.startWalkthrough(bid, request, selectionPayload(selectionStorage.get()))
      .then(settle)
      .catch((err) => {
        if (cancelled || epoch !== epochRef.current) return
        const { message, action } = errorMessage(err)
        setPhase({ kind: 'error', message, action })
      })

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
    // `phase` is deliberately absent: it is written by this effect, and the two states that
    // must not restart it (ready-for-this-code, error) are checked at the top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, eligibility.ok, bid, symbol, timeframe, spanKey, codeId, retryNonce])

  // ---- "Taking a while" ----------------------------------------------------
  // The user chose to never cap the drawn span, so a wide walkthrough can legitimately run for
  // minutes. Say so instead of looking stuck. Clearing the flag belongs to whoever changes the
  // phase (the start and exit effects above), so this only ever arms the timer.
  const startedAt = phase.kind === 'starting' ? phase.since : null
  useEffect(() => {
    if (startedAt == null) return undefined
    const remaining = Math.max(0, SLOW_AFTER_MS - (Date.now() - startedAt))
    const t = setTimeout(() => setSlowFor(startedAt), remaining)
    return () => clearTimeout(t)
  }, [startedAt])
  const slow = startedAt != null && slowFor === startedAt

  return { phase, eligibility, slow, retry }
}

/** How far the engine has got through the run's range, 0..100. */
function pctOf(progressBacktest: string | null | undefined, run: WalkthroughRun): number {
  if (!progressBacktest) return 0
  const ts = Math.floor(Date.parse(progressBacktest.replace(' ', 'T') + 'Z') / 1000)
  if (!Number.isFinite(ts)) return 0
  const total = run.rangeEndTs - run.rangeStartTs
  if (total <= 0) return 0
  return Math.max(0, Math.min(100, Math.round(((ts - run.rangeStartTs) / total) * 100)))
}
